import React, { useCallback, useEffect, useState } from "react";
import RebatePage from "../../entities/Rebate";
import { useAlert } from "../../context/AlertContext";
import AuthService from "../../api/AuthService";
import { useLocation } from "react-router-dom";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import dayjs from "dayjs";

const Rebate = () => {
  const { showAlert } = useAlert();
  const location = useLocation();
  const { taxCountry } = location.state || {};
  const [rebates, setRebates] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [taxPeriod, setTaxPeriod] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState("");

  const getTaxPeriod = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(taxCountry);
      if (result?.success) {
        setTaxPeriod(result?.data || []);
        const currentYear = new Date().getFullYear();
        const matchedYear = result?.data?.find((item) => {
          const startYear = dayjs(item?.start).year();
          return startYear === currentYear;
        });
        setSelectedTaxYear(matchedYear ? matchedYear._id : result?.data?.[0]?._id || "");
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  useEffect(() => {
    getTaxPeriod();
  }, []);

  const fetchRebates = useCallback(async () => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("DD-MM-YYYY") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("DD-MM-YYYY") : "";

      const response = await AuthService.getRebates({
        page,
        limit,
        country: taxCountry,
        start: startDate,
        end: endDate,
      });
      if (response?.success !== false) {
        const rebatesList = response?.data?.rebates || response?.rebates || [];
        setRebates(rebatesList);
        setTotal(response?.data?.total ?? response?.total ?? 0);
      } else {
        setRebates([]);
        setTotal(0);
        showAlert(response?.message || "Unable to fetch rebates.", "error");
      }
    } catch (error) {
      setRebates([]);
      setTotal(0);
      showAlert(error?.message || "Unable to fetch rebates.", "error");
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [limit, page, showAlert, taxCountry, selectedTaxYear, taxPeriod]);

  useEffect(() => {
    setPage(1);
  }, [taxCountry]);

  useEffect(() => {
    fetchRebates();
  }, [fetchRebates]);

  const handleAddRebate = async (rebatePayload) => {
    setAddLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("YYYY-MM-DD") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("YYYY-MM-DD") : "";

      const response = await AuthService.createRebate({
        ...rebatePayload,
        country: rebatePayload?.country || taxCountry,
        start: startDate,
        end: endDate,
      });
      if (response?.success !== false) {
        showAlert(response?.message || "Rebate created successfully.", "success");
        await fetchRebates();
        return true;
      }
      showAlert(response?.message || "Unable to create rebate.", "error");
      return false;
    } catch (error) {
      showAlert(error?.message || "Unable to create rebate.", "error");
      return false;
    } finally {
      setAddLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleUpdateRebate = async (rebateId, rebatePayload) => {
    if (!rebateId) return false;
    setUpdateLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);
      const startDate = selectedFY ? dayjs(selectedFY.start).format("YYYY-MM-DD") : "";
      const endDate = selectedFY ? dayjs(selectedFY.end).format("YYYY-MM-DD") : "";

      const response = await AuthService.updateRebate(rebateId, {
        ...rebatePayload,
        country: rebatePayload?.country || taxCountry,
        start: startDate,
        end: endDate,
      });
      if (response?.success !== false) {
        showAlert(response?.message || "Rebate updated successfully.", "success");
        await fetchRebates();
        return true;
      }
      showAlert(response?.message || "Unable to update rebate.", "error");
      return false;
    } catch (error) {
      showAlert(error?.message || "Unable to update rebate.", "error");
      return false;
    } finally {
      setUpdateLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleDeleteRebate = async (rebateId) => {
    if (!rebateId) return false;
    setDeleteLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const response = await AuthService.deleteRebate(rebateId);
      if (response?.success !== false) {
        showAlert(response?.message || "Rebate deleted successfully.", "success");
        await fetchRebates();
        return true;
      }
      showAlert(response?.message || "Unable to delete rebate.", "error");
      return false;
    } catch (error) {
      showAlert(error?.message || "Unable to delete rebate.", "error");
      return false;
    } finally {
      setDeleteLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleTaxYearChange = (event) => {
    const selectedYearId = event.target.value;
    setSelectedTaxYear(selectedYearId);
    setPage(1);
  };

  return (
    <RebatePage
      rebates={rebates}
      country={taxCountry}
      loading={loading}
      addLoading={addLoading}
      updateLoading={updateLoading}
      deleteLoading={deleteLoading}
      onAddRebate={handleAddRebate}
      onUpdateRebate={handleUpdateRebate}
      onDeleteRebate={handleDeleteRebate}
      page={page}
      limit={limit}
      total={total}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
      taxPeriod={taxPeriod}
      selectedTaxYear={selectedTaxYear}
      onTaxYearChange={handleTaxYearChange}
    />
  );
};

export default Rebate;
